// GL6 (#6) — aktivasyon bağlantısı gönder / tekrar gönder (Modal; EP + SP ortak). §4.3 / §4.4.
// Eski SP tekrar-gönder modalının yerini alır ve EP'ye tekrar gönderme + iki portala "bağlantı gönder" getirir.
// Hedef satırın UUID'sini TAŞIR; tekrar gönder anahtarı çağıranın o anki taze satırından (`invitationUuid`)
// gelir — yanıttaki YENİ uuid eskisini geçersiz kıldığından hiçbir yerde saklanmaz.
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Mail, MessageSquare, Send } from 'lucide-react';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  type ActivationLinkResult,
  type InviteChannel,
  type InviteContactChannel,
  type ResendActivationLinkRequest,
  type SendActivationLinkRequest,
  inviteContactIssues,
  inviteFieldErrors,
  toInviteContact,
} from '../api/contractors';
import { resolveApiErrorMessage } from '../i18n/apiError';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { Modal } from '../ui/Modal';
import { Select } from '../ui/Select';
import { Spinner } from '../ui/Spinner';
import { ActivationContactFields } from './ActivationContactFields';
import { ActivationLinkResultPanel } from './ActivationLinkResultPanel';
import type { PersonnelActivationClient } from './activationClient';
import { isLinkRateLimit, resolveActivationLinkError } from './activationLinkErrors';
import {
  addContactIssues,
  bothContactsFilled,
  inviteServerFieldErrors,
  isChannelUnavailable,
} from './personnelContact';

export type PersonnelLinkTarget =
  | {
      mode: 'send';
      userUuid: string;
      personName: string;
      /** `row.tcKimlikNo === null`. Maskeleme `null`'ı `null` bırakır; maskeli değer "T.C. var" demektir (DTO notu). */
      tcMissing: boolean;
      /** EP, `contractors:personal-data` yetkisiz: eksik T.C.'yi kendisi ekleyemez (R7). */
      personalDataMasked: boolean;
    }
  | {
      mode: 'resend';
      invitationUuid: string;
      personName: string;
      availableChannels: readonly InviteContactChannel[] | null;
    };

export interface PersonnelLinkModalProps {
  client: PersonnelActivationClient;
  /**
   * `null` = kapalı. Çağıran bunu pencere açılırken YAKALANAN durum olarak tutmalı ve YALNIZ `onClose`'dan
   * `null`'a çekmelidir; yenilenen listeden TÜRETMEMELİ (satır tazelenince hedef kaybolursa tek bağlantı kopyası gider).
   * Pencere son boş olmayan hedefin anlık görüntüsünden çizer.
   */
  target: PersonnelLinkTarget | null;
  /** Kapanışta çağrılır; başarıyla gönderildiyse sonucu (yeni `invitationUuid` dahil), yoksa `null` taşır. */
  onClose: (result: ActivationLinkResult | null) => void;
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

type Vars =
  | { mode: 'send'; userUuid: string; body: SendActivationLinkRequest }
  | { mode: 'resend'; invitationUuid: string; body: ResendActivationLinkRequest };

const SEND_DEFAULTS = { email: '', phone: '', channel: 'Both' as InviteChannel };

export function PersonnelLinkModal({
  client,
  target: incoming,
  onClose,
  restoreFocusRef,
}: PersonnelLinkModalProps) {
  const { t } = useTranslation();
  // Son boş olmayan hedef: kapanış animasyonunda başlık/gövde titremez, ebeveyn hedefi erken boşaltsa da çizim sürer.
  const latchedRef = useRef<PersonnelLinkTarget | null>(null);
  if (incoming) {
    latchedRef.current = incoming;
  }
  const target = latchedRef.current;
  const inflightRef = useRef(false);
  const resultRef = useRef<HTMLDivElement>(null);
  const [resendChannel, setResendChannel] = useState<InviteChannel>('Both');

  const schema = useMemo(
    () =>
      z
        .object({ email: z.string(), phone: z.string(), channel: z.enum(['Email', 'Sms', 'Both']) })
        .superRefine((values, ctx) => {
          addContactIssues(ctx, inviteContactIssues(values.email, values.phone), t);
        }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;
  const { register, handleSubmit, reset, watch, setError, setFocus, formState } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: SEND_DEFAULTS,
    });

  const mutation = useMutation<ActivationLinkResult, unknown, Vars>({
    mutationFn: (vars) =>
      vars.mode === 'send'
        ? client.sendLink(vars.userUuid, vars.body)
        : client.resend(vars.invitationUuid, vars.body),
    onError: (error, vars) => {
      if (vars.mode !== 'send') {
        return;
      }
      const fieldErrors = inviteServerFieldErrors(error, t);
      for (const fieldError of fieldErrors) {
        setError(fieldError.field, { type: 'server', message: fieldError.message });
      }
      if (fieldErrors.length > 0) {
        setFocus(fieldErrors.some((f) => f.field === 'email') ? 'email' : 'phone');
      }
    },
    // Başarısız çağrı da satırı değiştirmiş olabilir (bayat uuid → 404, bekleme saati) → HER durumda tazele.
    onSettled: () => {
      inflightRef.current = false;
      client.invalidate();
    },
  });
  const { reset: resetMutation } = mutation;

  const [emailValue, phoneValue] = watch(['email', 'phone']);
  const bothContacts = bothContactsFilled(emailValue, phoneValue);
  const submitting = mutation.isPending;
  const success = mutation.isSuccess ? mutation.data : null;

  const sendFieldErrorCount =
    target?.mode === 'send' ? inviteFieldErrors(mutation.error).length : 0;
  const channelError =
    target?.mode === 'send' && isChannelUnavailable(mutation.error, bothContacts)
      ? resolveApiErrorMessage(mutation.error, t)
      : null;
  // D-a: send-link 409 backend metni "başka bir hesapta kayıtlı" der ve değişmez; `t(code)` ASLA gösterilmez.
  // Alan listesi varsa alan hatası, yoksa nötr genel metin.
  const bannerError =
    mutation.isError && sendFieldErrorCount === 0 && channelError === null
      ? resolveActivationLinkError(mutation.error, t)
      : null;

  const bannerRef = useRef<HTMLDivElement>(null);
  // Alan dışı sunucu hatası (403, ağ, bayat bağlantı) ekran dışında kalabilir ve `role="alert"` odağı taşımaz →
  // görünür yap, odağı banner'a al. Gönder formunda 429 hariç: iletişim bilgisi düzenlenecek, alanlar odakta kalır.
  const focusBanner =
    bannerError !== null && !(target?.mode === 'send' && isLinkRateLimit(mutation.error));
  useEffect(() => {
    if (focusBanner) {
      bannerRef.current?.scrollIntoView?.({ block: 'nearest' });
      bannerRef.current?.focus();
    }
  }, [focusBanner]);

  useEffect(() => {
    if (success) {
      resultRef.current?.focus();
    }
  }, [success]);

  function close() {
    // Bağlantı bellekten düşer; sonraki hedef temiz açılır.
    resetMutation();
    reset(SEND_DEFAULTS);
    setResendChannel('Both');
    inflightRef.current = false;
    onClose(success);
  }
  function handleOpenChange(next: boolean) {
    if (!next && !mutation.isPending) {
      close();
    }
  }

  const resend = target?.mode === 'resend' ? target : null;
  const canChoose = resend?.availableChannels?.length === 2;
  const noContact = resend?.availableChannels?.length === 0;
  const fixedChannel =
    resend?.availableChannels?.length === 1 ? resend.availableChannels[0] : undefined;

  function submitResend() {
    if (!resend || noContact || inflightRef.current) {
      return;
    }
    inflightRef.current = true;
    mutation.mutate({
      mode: 'resend',
      invitationUuid: resend.invitationUuid,
      body: canChoose ? { channel: resendChannel } : {},
    });
  }

  const isSend = target?.mode === 'send';
  // R7: T.C.'si olmayan kişiye bağlantı gönderilemez (`409 PERSONNEL_TC_REQUIRED`) — iletişim yazdırıp sonra
  // reddetmek yerine baştan uyar ve gönderi kapat (tekrar gönderin `noContact` kalıbı).
  const sendBlocked = target?.mode === 'send' && target.tcMissing;
  const submitLabel = t(isSend ? 'personnelLink.send.submit' : 'personnelLink.resend.submit');

  return (
    <Modal
      open={incoming !== null}
      onOpenChange={handleOpenChange}
      title={t(isSend ? 'personnelLink.send.title' : 'personnelLink.resend.title')}
      description={
        success || target === null
          ? undefined
          : t(isSend ? 'personnelLink.send.description' : 'personnelLink.resend.description', {
              name: target.personName,
            })
      }
      size="md"
      closeLabel={t('common.close')}
      restoreFocusRef={restoreFocusRef}
      footerColumns={success ? 1 : 2}
      footer={
        success ? (
          <Button onClick={close}>{t('common.close')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            {isSend ? (
              <Button
                type="submit"
                form="personnel-link-form"
                loading={submitting}
                disabled={submitting || sendBlocked}
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {submitting ? <Spinner label={submitLabel} /> : submitLabel}
              </Button>
            ) : (
              <Button
                onClick={submitResend}
                loading={submitting}
                disabled={submitting || noContact}
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {submitting ? <Spinner label={submitLabel} /> : submitLabel}
              </Button>
            )}
          </>
        )
      }
    >
      {success ? (
        <div
          ref={resultRef}
          tabIndex={-1}
          // biome-ignore lint/a11y/useSemanticElements: programatik odak hedefi; <fieldset> kenarlık/min-width sıfırlaması gerektirir
          role="group"
          aria-labelledby="personnel-link-result-hint"
          className="flex flex-col gap-4 focus:outline-none"
        >
          <Alert tone="success">
            <span id="personnel-link-result-hint">{t('personnelLink.success.title')}</span>
          </Alert>
          <p className="text-sm text-muted">{t('personnelLink.success.body')}</p>
          <ActivationLinkResultPanel result={success} />
        </div>
      ) : isSend ? (
        <form
          id="personnel-link-form"
          className="flex flex-col gap-4"
          noValidate
          onSubmit={handleSubmit((values) => {
            if (target?.mode !== 'send' || target.tcMissing || inflightRef.current) {
              return;
            }
            const contact = toInviteContact(values.email, values.phone, values.channel);
            if (contact === null) {
              return; // zod iki boş iletişimi zaten keser
            }
            inflightRef.current = true;
            mutation.mutate({
              mode: 'send',
              userUuid: target.userUuid,
              body: {
                email: contact.email ?? null,
                phone: contact.phone ?? null,
                ...(contact.channel ? { channel: contact.channel } : {}),
              },
            });
          })}
        >
          {target?.mode === 'send' && target.tcMissing ? (
            <Alert tone="warning">
              <span className="flex flex-col gap-1">
                <span>{t('personnelLink.send.tcMissing')}</span>
                {target.personalDataMasked ? (
                  <span>{t('personnelForm.lock.tcNoPermission')}</span>
                ) : null}
              </span>
            </Alert>
          ) : (
            <>
              {bannerError ? (
                <div ref={bannerRef} tabIndex={-1} className="focus:outline-none">
                  <Alert tone="danger">{bannerError}</Alert>
                </div>
              ) : null}
              <ActivationContactFields
                idPrefix="personnel-link"
                emailProps={register('email', { deps: ['phone'] })}
                phoneProps={register('phone', { deps: ['email'] })}
                channelProps={register('channel')}
                emailError={formState.errors.email?.message}
                phoneError={formState.errors.phone?.message}
                channelError={channelError}
                showChannel={bothContacts}
                emailHint={t('personnelLink.send.contactHint')}
              />
            </>
          )}
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          {bannerError ? (
            <div ref={bannerRef} tabIndex={-1} className="focus:outline-none">
              <Alert tone="danger">{bannerError}</Alert>
            </div>
          ) : null}
          {noContact ? <Alert tone="warning">{t('personnelLink.resend.noContact')}</Alert> : null}
          {canChoose ? (
            <Field
              htmlFor="personnel-link-resend-channel"
              label={t('personnelLink.field.channel')}
              hint={t('personnelLink.resend.channelHint')}
            >
              <Select
                id="personnel-link-resend-channel"
                options={[
                  { value: 'Email', label: t('personnelLink.channel.email') },
                  { value: 'Sms', label: t('personnelLink.channel.sms') },
                  { value: 'Both', label: t('personnelLink.channel.both') },
                ]}
                value={resendChannel}
                aria-describedby="personnel-link-resend-channel-hint"
                onChange={(e) => setResendChannel(e.currentTarget.value as InviteChannel)}
              />
            </Field>
          ) : null}
          {fixedChannel ? (
            <p className="flex items-center gap-2 text-sm text-fg">
              {fixedChannel === 'Email' ? (
                <Mail className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
              ) : (
                <MessageSquare className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
              )}
              {t(
                fixedChannel === 'Email'
                  ? 'personnelLink.resend.via.email'
                  : 'personnelLink.resend.via.sms',
              )}
            </p>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
